<template>
  <Transition
    enter-active-class="transition-opacity duration-200 ease-out" enter-from-class="opacity-0"
    leave-active-class="transition-opacity duration-150 ease-in" leave-to-class="opacity-0">
    <div v-if="open" class="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="event-privacy-title">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" data-test="privacy-backdrop" @click="$emit('close')"></div>

      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-md w-full p-6">
        <h3 id="event-privacy-title" class="text-lg font-bold text-gray-900 dark:text-white mb-1">Event privacy</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">Control who can see and book this event.</p>

        <div class="space-y-3">
          <label v-for="option in options" :key="option.key" :data-test="`privacy-option-${option.key}`"
            class="flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors"
            :class="choice === option.value
              ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/10'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'">
            <input type="radio" name="event-privacy" :value="option.value" v-model="choice" class="sr-only" />
            <div class="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" :class="option.iconWrap">
              <svg class="w-5 h-5" :class="option.iconColor" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" :d="option.icon" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-sm text-gray-900 dark:text-white">{{ option.title }}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ option.description }}</p>
            </div>
            <div class="ml-auto flex-shrink-0 mt-0.5">
              <div v-if="choice === option.value" class="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0Z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div v-else class="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
            </div>
          </label>
        </div>

        <div class="flex gap-3 mt-6">
          <button type="button" data-test="privacy-cancel" @click="$emit('close')"
            class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button type="button" data-test="privacy-save" :disabled="saving" @click="$emit('save', choice)"
            class="flex-1 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
            {{ saving ? 'Saving' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch } from 'vue'

// Presentational only: the parent owns the API call so the events list and the
// per-event header can each update their own state after saving.
const props = defineProps({
  open: { type: Boolean, default: false },
  isPublic: { type: Boolean, default: true },
  saving: { type: Boolean, default: false },
})
defineEmits(['close', 'save'])

const choice = ref(props.isPublic)

// Every open starts from the saved value, not whatever was picked last time.
watch(() => props.open, (open) => {
  if (open) choice.value = props.isPublic
})

const options = [
  {
    key: 'public',
    value: true,
    title: 'Public',
    description: 'Anyone with the link can book this event',
    iconWrap: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    icon: 'M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm-1.5-9.5a1.5 1.5 0 1 1 3 0v.5h.5a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2h.5v-.5Z',
  },
  {
    key: 'private',
    value: false,
    title: 'Private',
    description: 'Only visible to people you share it with directly',
    iconWrap: 'bg-gray-100 dark:bg-gray-800',
    iconColor: 'text-gray-500 dark:text-gray-400',
    icon: 'M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z',
  },
]
</script>
